import {
  collection,
  deleteDoc,
  deleteField,
  doc,
  increment,
  onSnapshot,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import type { DocumentReference, Unsubscribe } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import { sanitizeHtml } from '../utils/sanitizeHtml';
import {
  SNIPPET_VARIABLES,
  findUnknownVariables,
  htmlToPlainText,
  unfilledTokenText,
} from '../pages/mail/utils/snippetVars';

/**
 * Reply snippets: one SHARED library (both app users see and edit the same
 * `mail_snippets` docs - the owner and his wife answer the same client
 * questions). Plain client reads/writes: the repo's catch-all rule already lets
 * any signed-in user read/write a root collection that is not on its server-only
 * exclusion list, and mail_snippets is not on it.
 *
 * Doc: { title, shortcut?, bodyHtml, bodyText, createdBy, createdAt, updatedAt,
 *        useCount, lastUsedAt }
 *
 * Nothing is seeded: an empty library stays empty until the owner writes one.
 */

export const SNIPPETS_COLLECTION = 'mail_snippets';
export const SNIPPET_TITLE_MAX = 80;
export const SNIPPET_SHORTCUT_MAX = 32;
/** Well under Firestore's 1 MiB doc cap (html + text are both stored); a canned reply is never this long. */
export const SNIPPET_BODY_MAX_BYTES = 20 * 1024;
/**
 * How long a save/delete may wait for the server's acknowledgement. Firestore
 * writes do not fail offline, they just stay pending until the connection is back,
 * so without a limit the form would sit on "saving..." indefinitely.
 */
export const SNIPPET_WRITE_TIMEOUT_MS = 12_000;
const SHORTCUT_RE = /^[a-z0-9-]+$/;

type FirestoreTime = { toMillis?: () => number } | null;

export interface Snippet {
  id: string;
  title: string;
  shortcut?: string;
  bodyHtml: string;
  bodyText: string;
  createdBy: string;
  createdAt: FirestoreTime;
  updatedAt: FirestoreTime;
  useCount: number;
  lastUsedAt: FirestoreTime;
  /**
   * Set only when the stored bodyHtml is over SNIPPET_BODY_MAX_BYTES. The rules do not
   * validate a doc's shape, so another user or a script can write one the form would
   * never save. It is still listed (hiding it would look like data loss) and its body
   * is NEVER truncated; consumers decide what to do with it (warn, refuse to insert).
   */
  oversize?: true;
}

export interface SnippetInput {
  title: string;
  shortcut?: string;
  bodyHtml: string;
}

export type SnippetField = 'title' | 'shortcut' | 'body';

export type SnippetValidation =
  | { ok: true; value: { title: string; shortcut?: string; bodyHtml: string; bodyText: string } }
  | { ok: false; errors: Partial<Record<SnippetField, string>> };

export class SnippetValidationError extends Error {
  errors: Partial<Record<SnippetField, string>>;
  constructor(errors: Partial<Record<SnippetField, string>>) {
    super(Object.values(errors)[0] || 'invalid snippet');
    this.name = 'SnippetValidationError';
    this.errors = errors;
  }
}

/**
 * A write that cannot be acknowledged right now (browser offline, or no answer
 * within SNIPPET_WRITE_TIMEOUT_MS). `code` mirrors Firestore's own 'unavailable' so
 * the UI's "check your connection" branch and its logging treat it like one.
 */
export class SnippetOfflineError extends Error {
  code: string;
  constructor() {
    super('no connection - the snippet change did not reach the server');
    this.name = 'SnippetOfflineError';
    this.code = 'unavailable';
  }
}

/** UTF-8 byte length (Firestore sizes strings this way, not in UTF-16 units). */
function utf8Bytes(s: string): number {
  let n = 0;
  for (let i = 0; i < s.length; i += 1) {
    const c = s.charCodeAt(i);
    if (c < 0x80) n += 1;
    else if (c < 0x800) n += 2;
    else if (c >= 0xd800 && c <= 0xdbff) { n += 4; i += 1; } // surrogate pair
    else n += 3;
  }
  return n;
}

/**
 * Size of a snippet body as Firestore counts it (UTF-8 bytes of the html string).
 * The ONE way the limit is measured: the save check below, the stored-doc oversize
 * flag and any size hint in the UI must all agree on where "too long" starts.
 */
export function snippetBodyBytes(html: string): number {
  return utf8Bytes(html);
}

/** The variables a snippet may use, as the owner types them: "{{firstName}}, {{fullName}}, ...". */
const VALID_PLACEHOLDERS = SNIPPET_VARIABLES.map((v) => `{{${v.name}}}`).join(', ');

/**
 * Check + normalise what the owner typed. The HTML is sanitised HERE (and again
 * before every insertion): a snippet body ends up inside the editor, and from
 * there in a client email, so it must never carry script or handlers no matter
 * how the doc got written.
 */
export function validateSnippetInput(input: { title?: unknown; shortcut?: unknown; bodyHtml?: unknown }): SnippetValidation {
  const errors: Partial<Record<SnippetField, string>> = {};

  const title = String(input.title ?? '').replace(/\s+/g, ' ').trim();
  if (!title) errors.title = 'give the snippet a title';
  else if (title.length > SNIPPET_TITLE_MAX) errors.title = `title is too long (max ${SNIPPET_TITLE_MAX} characters)`;

  const shortcut = String(input.shortcut ?? '').trim().toLowerCase();
  if (shortcut && (shortcut.length > SNIPPET_SHORTCUT_MAX || !SHORTCUT_RE.test(shortcut))) {
    errors.shortcut = `shortcut: lowercase letters, numbers and dashes only (max ${SNIPPET_SHORTCUT_MAX})`;
  }

  const bodyHtml = sanitizeHtml(String(input.bodyHtml ?? ''), { noStyleTags: true });
  const bodyText = htmlToPlainText(bodyHtml);
  if (!bodyText.trim()) errors.body = 'the snippet text is empty';
  else if (snippetBodyBytes(bodyHtml) > SNIPPET_BODY_MAX_BYTES) {
    errors.body = `snippet is too long (max ${SNIPPET_BODY_MAX_BYTES / 1024} KB)`;
  } else {
    // A typo'd placeholder ("{{first name}}") can only ever insert as a literal gap, and
    // the composer then refuses to send - so refuse to SAVE it, while the owner is
    // still looking at the text, not when a client is waiting. Checked on the
    // sanitised html: that is exactly what would be stored and later inserted.
    const unknown = findUnknownVariables(bodyHtml);
    if (unknown.length > 0) {
      errors.body = `unknown placeholder${unknown.length > 1 ? 's' : ''} ${unknown.map(unfilledTokenText).join(', ')} `
        + `- use one of ${VALID_PLACEHOLDERS}`;
    }
  }

  if (Object.keys(errors).length > 0) return { ok: false, errors };
  return { ok: true, value: { title, ...(shortcut ? { shortcut } : {}), bodyHtml, bodyText } };
}

const currentUid = (): string => {
  try {
    return auth.currentUser?.uid || localStorage.getItem('user_uid') || '';
  } catch {
    return auth.currentUser?.uid || '';
  }
};

const errCode = (err: unknown): string => String((err as { code?: unknown })?.code || 'error');

/** The browser itself reports no network: a write now could only sit in the SDK's queue. */
const browserOffline = (): boolean => typeof navigator !== 'undefined' && navigator.onLine === false;

/**
 * Reject instead of hanging when the server never acknowledges `write`. NOTE the
 * write itself cannot be cancelled: after a timeout it is still queued inside the
 * SDK and lands when the connection returns. That is fine for update/delete (same
 * end state) and createSnippet makes its retry idempotent (see pendingCreates).
 */
function withWriteTimeout<T>(write: Promise<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new SnippetOfflineError()), SNIPPET_WRITE_TIMEOUT_MS);
    write.then(
      (value) => { clearTimeout(timer); resolve(value); },
      (err) => { clearTimeout(timer); reject(err); },
    );
  });
}

/**
 * Doc refs of creates that have been sent but not yet acknowledged, keyed by the
 * exact content + author. A create that timed out is still queued and WILL land
 * later, so the owner's "try again" with the same text must reuse its doc id
 * (setDoc on an existing id is idempotent) or the snippet would be written twice.
 * An entry lives only while its write is in flight.
 */
const pendingCreates = new Map<string, DocumentReference>();

/** Most used first (the ones you reach for every day), then A-Z. */
export function sortSnippets(list: Snippet[]): Snippet[] {
  return list.slice().sort((a, b) => {
    if (b.useCount !== a.useCount) return b.useCount - a.useCount;
    return a.title.localeCompare(b.title, undefined, { sensitivity: 'base' });
  });
}

function toSnippet(id: string, data: Record<string, unknown> | undefined): Snippet | null {
  if (!data || typeof data.title !== 'string') return null;
  const bodyHtml = typeof data.bodyHtml === 'string' ? data.bodyHtml : '';
  const bodyText = typeof data.bodyText === 'string' && data.bodyText ? data.bodyText : htmlToPlainText(bodyHtml);
  if (!bodyHtml && !bodyText) return null;
  const count = Number(data.useCount);
  // Flag, never trim: a doc over the limit is still the owner's text.
  const oversize = snippetBodyBytes(bodyHtml) > SNIPPET_BODY_MAX_BYTES;
  return {
    id,
    title: data.title,
    ...(typeof data.shortcut === 'string' && data.shortcut ? { shortcut: data.shortcut } : {}),
    bodyHtml,
    bodyText,
    createdBy: typeof data.createdBy === 'string' ? data.createdBy : '',
    createdAt: (data.createdAt as FirestoreTime) ?? null,
    updatedAt: (data.updatedAt as FirestoreTime) ?? null,
    useCount: Number.isFinite(count) && count > 0 ? count : 0,
    lastUsedAt: (data.lastUsedAt as FirestoreTime) ?? null,
    ...(oversize ? { oversize: true as const } : {}),
  };
}

export interface SnippetsMeta {
  /**
   * True while the list comes from the local cache alone. Offline with nothing
   * cached the SDK still delivers a snapshot - an EMPTY one that says nothing about
   * the real library - so callers must not read "empty + fromCache" as "no snippets
   * yet" (show a loading/offline state instead). It turns false once the server
   * has answered.
   */
  fromCache: boolean;
}

/**
 * Live library, already sorted. `onError` gets a short human sentence (the raw
 * error stays out of logs and UI). Returns the unsubscribe.
 *
 * Metadata changes are requested on purpose: without them the fromCache -> false
 * flip is swallowed whenever the server's answer leaves the list unchanged, so a
 * genuinely empty library would never re-deliver after coming back online and the
 * caller would stay on its offline state for good.
 */
export function subscribeToSnippets(
  cb: (snippets: Snippet[], meta: SnippetsMeta) => void,
  onError?: (message: string) => void,
): Unsubscribe {
  return onSnapshot(
    collection(db, SNIPPETS_COLLECTION),
    { includeMetadataChanges: true },
    (snap) => {
      const list: Snippet[] = [];
      snap.docs.forEach((d) => {
        const s = toSnippet(d.id, d.data() as Record<string, unknown>);
        if (s) list.push(s);
      });
      cb(sortSnippets(list), { fromCache: snap.metadata.fromCache });
    },
    (err) => {
      const code = errCode(err);
      console.warn('[snippets] subscription failed:', code);
      onError?.(code === 'permission-denied'
        ? "you don't have access to the snippet library"
        : 'could not load snippets - check your connection');
    },
  );
}

export async function createSnippet(input: SnippetInput): Promise<string> {
  const v = validateSnippetInput(input);
  if (!v.ok) throw new SnippetValidationError(v.errors);
  const uid = currentUid();
  if (!uid) throw new Error('not signed in');
  // Offline: fail now rather than queue a write the owner was told failed.
  if (browserOffline()) throw new SnippetOfflineError();
  const key = JSON.stringify([uid, v.value.title, v.value.shortcut ?? '', v.value.bodyHtml]);
  const ref = pendingCreates.get(key) ?? doc(collection(db, SNIPPETS_COLLECTION));
  const write = setDoc(ref, {
    title: v.value.title,
    ...(v.value.shortcut ? { shortcut: v.value.shortcut } : {}),
    bodyHtml: v.value.bodyHtml,
    bodyText: v.value.bodyText,
    createdBy: uid,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    useCount: 0,
    lastUsedAt: null,
  });
  // Remembered until the write settles - even long after withWriteTimeout stopped waiting.
  pendingCreates.set(key, ref);
  const settled = () => { pendingCreates.delete(key); };
  write.then(settled, settled);
  await withWriteTimeout(write);
  return ref.id;
}

export async function updateSnippet(id: string, input: SnippetInput): Promise<void> {
  if (!id) throw new Error('missing snippet id');
  const v = validateSnippetInput(input);
  if (!v.ok) throw new SnippetValidationError(v.errors);
  if (browserOffline()) throw new SnippetOfflineError();
  await withWriteTimeout(updateDoc(doc(db, SNIPPETS_COLLECTION, id), {
    title: v.value.title,
    // Clearing the shortcut must REMOVE the field (an undefined value is rejected by Firestore).
    shortcut: v.value.shortcut ?? deleteField(),
    bodyHtml: v.value.bodyHtml,
    bodyText: v.value.bodyText,
    updatedAt: serverTimestamp(),
  }));
}

export async function deleteSnippet(id: string): Promise<void> {
  if (!id) throw new Error('missing snippet id');
  if (browserOffline()) throw new SnippetOfflineError();
  await withWriteTimeout(deleteDoc(doc(db, SNIPPETS_COLLECTION, id)));
}

/**
 * Bump the usage counters that order the list. Best-effort by design: a failed
 * bump must never surface in (or block) the composer the snippet was just
 * inserted into, so this swallows everything.
 */
export async function recordSnippetUse(id: string): Promise<void> {
  if (!id) return;
  try {
    await updateDoc(doc(db, SNIPPETS_COLLECTION, id), {
      useCount: increment(1),
      lastUsedAt: serverTimestamp(),
    });
  } catch (err) {
    console.warn('[snippets] could not record use:', errCode(err));
  }
}
